import { useTimer } from '@/contexts/TimerContext';
import { useModals } from '@/contexts/ModalContext';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, Timer } from 'lucide-react';
import { cn } from '@/lib/utils';

export function FloatingTimer() {
  const { timeLeft, status, pause, resume, reset } = useTimer();
  const { openTimer, timerOpen } = useModals();

  // Don't show when timer modal is open or when idle
  if (timerOpen || status === 'idle') {
    return null;
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
      <div 
        className={cn(
          "flex items-center gap-2 px-4 py-3 rounded-full shadow-lg border backdrop-blur-sm cursor-pointer transition-all hover:scale-105",
          status === 'break' 
            ? "bg-accent/90 border-accent text-accent-foreground" 
            : status === 'finished'
            ? "bg-success/90 border-success text-success-foreground"
            : "bg-primary/90 border-primary text-primary-foreground"
        )}
        onClick={() => openTimer()}
      >
        <Timer className="h-5 w-5" />
        <span className={cn(
          "text-lg font-bold tabular-nums",
          status === 'running' && "animate-pulse"
        )}>
          {formatTime(timeLeft)}
        </span>
        
        <div className="flex items-center gap-1 ml-2 border-l border-current/20 pl-2" onClick={(e) => e.stopPropagation()}>
          {status === 'running' && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-current hover:bg-white/20"
              onClick={pause}
            >
              <Pause className="h-4 w-4" />
            </Button>
          )}
          
          {status === 'paused' && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-current hover:bg-white/20"
              onClick={resume}
            >
              <Play className="h-4 w-4" />
            </Button>
          )}
          
          {(status === 'running' || status === 'paused' || status === 'break') && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-current hover:bg-white/20"
              onClick={reset}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          
          {status === 'finished' && (
            <span className="text-sm font-medium px-2">Concluído!</span>
          )}
        </div>
      </div>
    </div>
  );
}
